import useToast from '@hooks/ui/useToast';
import { PaymentErrorType, PrepaidPaymentErrorSnapshot } from '@models/error';
import { CreateOrderRequest } from '@models/order';
import { useFlow } from '@router/stackflow';
import { orderActions } from '@store/order/orderSlice';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

interface Props {
    type: PaymentErrorType;
    payload: string;
}

function usePaymentFailed({ type, payload }:Props) {
    const { t, i18n } = useTranslation('errors');
    const { push, replace } = useFlow();
    const toast = useToast();
    const [cookies] = useCookies(['tempOrder'], { doNotParse: true });
    const dispatch = useDispatch();
    const lng = navigator.language;

    const handleTry = (func: Function) => {
        try {
            func();
        } catch {
            toast.error(t('toast.dataLoadError', { lng }));
            replace('MainActivity', {});
        }
    };
    const handleOnPrepayCharge = () => {
        handleTry(() => {
            const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
            const { prepaidCardId, amount } = data;
            if (prepaidCardId) {
                toast.error(t('toast.prepaidChargeError', { lng }));
                replace('MainActivity', {}, { animate: false });
                push('MyPrepaidCardActivity', {}, { animate: false });
                push('PrepaidCardChargeActivity', { prepaidCardId, amount });
            } else {
                toast.error(t('toast.prepaidNotFound', { lng }));
                replace('MainActivity', {});
            }
        });
    };

    const handleOnPrepayPurchase = () => {
        handleTry(() => {
            const data: PrepaidPaymentErrorSnapshot = JSON.parse(payload);
            const { cardImageId, cardName, amount } = data;
            if (cardImageId) {
                toast.info(t('toast.prepaidPurchaseError', { lng }));
                replace('MainActivity', {}, { animate: false });
                push('MyPrepaidCardActivity', {}, { animate: false });
                push('PrepaidCardImagesActivity', { type: 'BUY' }, { animate: false });
                push('PrepaidCardImageActivity', {
                    prepaidCardImageId: cardImageId,
                    cardName,
                    amount,
                });
            } else {
                toast.error(t('toast.prepaidNotFound', { lng }));
                replace('MainActivity', {});
            }
        });
    };

    const handleOnPrepayFail = () => {
        toast.error(t('toast.paymentError', { lng }));
        replace('MainActivity', {});
    };

    const handleOnOrderFail = () => {
        handleTry(() => {
            toast.info(t('toast.paymentCancelled', { lng }));
            const tempOrder: CreateOrderRequest = JSON.parse(cookies.tempOrder);
            const { shopId } = tempOrder;
            const itemId = tempOrder.orderLines.map((line) => line.shopItemId)
                ?.[0];
            replace('MainActivity', {}, { animate: false });
            push('ShopListActivity', { type: 'LIST', isOrder: true }, { animate: false });
            push('ItemsActivity', { type: 'ALL', shopId }, { animate: false });
            push('ItemActivity', { itemId, shopId }, { animate: false });
            push('OrderActivity', { }, { animate: false });
            dispatch(orderActions.copy(JSON.parse(cookies.tempOrder!)));
        });
    };

    useEffect(() => {
        switch (type) {
        case 'PREPAY_CHARGE':
            handleOnPrepayCharge();
            break;
        case 'PREPAY_PURCHASE':
            handleOnPrepayPurchase();
            break;
        case 'PREPAY_FAIL':
            handleOnPrepayFail();
            break;
        case 'ORDER':
            handleOnOrderFail();
            break;
        default:
            throw new Error(t('toast.unsupportedPaymentType'));
        }
    }, [type, payload, t]);
}

export default usePaymentFailed;
